import {
    ClaimType,
    formatDate,
    formatNumber,
    GetGrossCollectionsReportRequest,
    GetPatientCollectionsReportRequest,
    GetPayerMixReportRequest,
    GetPracticeSummaryReportRequest,
    IDataset,
    MetricType,
    ProviderReportItem,
    ReportLabelType,
    Sentiment,
    SpanType,
} from '@enter/core';
import { IChartTimeline, IPayerMixChart } from '../components';
import * as d3 from 'd3';
import _ from 'lodash';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { IFeatures } from './root.store';
import type { MetricsGenerator } from './metrics.generator';
import { MetricsService } from './metrics.service';
import { buildCacheKey, getInterval, handleError, pri2ict, pri2ipmc } from './util';

export interface IFormulaProps {
    [key: string]: number;
}

export class MetricsStore {
    @observable public datasets: IDataset[] = [];
    @observable public metrics: MetricType[];
    @observable public selectedClaimTypes: ClaimType[] = [ClaimType.PROFESSIONAL];

    @observable public practiceSummaryTimeline?: IChartTimeline | Error;
    @observable public patientCollectionsTimeline?: IChartTimeline | Error;
    @observable public claimVolumeTimeline?: IChartTimeline | Error;
    @observable public payerMixChart?: IPayerMixChart | Error;

    private features: IFeatures;
    private datasetService: MetricsService = new MetricsService();
    private datasetGenerator: MetricsGenerator;
    private datasetIndex: Map<string, ProviderReportItem[][] | undefined> = new Map();

    constructor(features: IFeatures, datasetGenerator: MetricsGenerator) {
        const savedClaimTypes = window.localStorage.getItem('dashboard_claim_types');
        if (savedClaimTypes) {
            this.selectedClaimTypes = JSON.parse(savedClaimTypes) || [ClaimType.PROFESSIONAL];
            if(this.selectedClaimTypes && this.selectedClaimTypes.length > 1) {
                this.selectedClaimTypes = [ClaimType.PROFESSIONAL];
            }
        }

        this.features = features;
        this.datasetGenerator = datasetGenerator;

        const storedMetrics = window.localStorage.getItem('metrics');
        if (storedMetrics) {
            const rawMetrics: MetricType[] = JSON.parse(storedMetrics);
            const filteredMetrics = _.filter(rawMetrics, metric => {
                return _.includes(_.values(MetricType), metric);
            });
            if (_.size(filteredMetrics) === 4) {
                this.metrics = filteredMetrics;
            } else {
                // NB: replace unused items
                this.metrics = _.take(_.concat(filteredMetrics, _.values(MetricType)), 4) as MetricType[];
                window.localStorage.setItem('metrics', JSON.stringify(this.metrics));
            }
        } else {
            this.metrics = [
                MetricType.GROSS_CHARGE,
                MetricType.CLAIMS_SUBMITTED,
                MetricType.GROSS_PATIENT_PAID,
                MetricType.GROSS_PAYER_PAID,
                MetricType.PAYER_EXPECTED,
            ];
        }

        // Last, after every field above has its initial value.
        //
        // Without this the `@observable` fields are annotations and nothing more: MobX 6 only
        // applies decorators when `makeObservable` runs, so `runInAction(() => this.claimVolume
        // Timeline = ...)` assigned a plain property and no `observer` re-rendered. The loads all
        // worked; the screen simply never heard about them, so every card sat on its spinner
        // forever and the Overview looked like it was still fetching.
        //
        // Seen, not deduced: clicking a granularity chip forced the card to re-render on its own
        // setState and "Total Claims Volume" appeared instantly, with data that had been sitting
        // in the store the whole time. That is also why this page was never routed.
        makeObservable(this);
    }

    @action
    public selectClaimType = (type: ClaimType) => {
        //Functionality to allow multi-select
        //let types = _.xor<ClaimType>(this.selectedClaimTypes, [type]);
        // if (types.length === 0) {
        //     types = [ClaimType.PROFESSIONAL, ClaimType.INSTITUTIONAL].filter(el => el !== type);
        // }

        this.selectedClaimTypes = [ClaimType.PROFESSIONAL, ClaimType.INSTITUTIONAL].filter(el => el === type); //types

        this.saveClaimTypes();
    };

    private readonly saveClaimTypes = (): void => {
        window.localStorage.setItem('dashboard_claim_types', JSON.stringify(this.selectedClaimTypes));
    };

    @action
    public replaceMetric(index: number, metric: MetricType): void {
        if (index > -1 && index < this.metrics.length) {
            this.metrics[index] = metric;
            window.localStorage.setItem('metrics', JSON.stringify(this.metrics));
        }
    }

    @action public loadMetricsData = (
        metrics: MetricType[],
        startDate: Date,
        endDate: Date,
        claimTypes: ClaimType[],
    ): Promise<void> => {
        return this.handleLoading(
            async (): Promise<void> => {
                this.datasets = []; // NB: zero out metrics to trigger load animations
                const [submitted, expected, gross] = await Promise.all([
                    this.loadSubmitted(startDate, endDate, claimTypes),
                    this.loadExpected(startDate, endDate, claimTypes),
                    this.loadGross(startDate, endDate, claimTypes),
                ]);
                const datasets = await Promise.all(
                    _.map(metrics, metric => this.loadMetric(metric, submitted, expected, gross)),
                );
                runInAction(() => {
                    this.datasets = _.compact(datasets);
                });
            },
        );
    };

    private loadSubmitted = (startDate: Date, endDate: Date, claimTypes: ClaimType[]) => {
        const interval = getInterval(SpanType.DAYOFYEAR);

        return this.findOrFetch(
            'claimsSubmitted',
            interval,
            startDate,
            endDate,
            claimTypes,
            this.datasetService.fetchClaimsSubmittedReport,
            this.datasetGenerator.generateClaimsSubmittedReport,
        );
    };

    private loadExpected = (startDate: Date, endDate: Date, claimTypes: ClaimType[]) => {
        const interval = getInterval(SpanType.DAYOFYEAR);

        return this.findOrFetch(
            'claimsExpectedRate',
            interval,
            startDate,
            endDate,
            claimTypes,
            this.datasetService.fetchPayerExpectedReport,
            this.datasetGenerator.generatePayerExpectedReport,
        );
    };

    private loadGross = (startDate: Date, endDate: Date, claimTypes: ClaimType[]) => {
        const interval = getInterval(SpanType.DAYOFYEAR);

        return this.findOrFetch(
            'gross',
            interval,
            startDate,
            endDate,
            claimTypes,
            this.datasetService.fetchGrossCollectionReport,
            this.datasetGenerator.generateGrossCollectionReport,
        );
    };

    @action public loadPayerMixChart = (startDate: Date, endDate: Date, claimTypes: ClaimType[]): Promise<void> => {
        return this.handleLoading(
            async (): Promise<void> => {
                let payerMixChart: IPayerMixChart | Error;
                try {
                    const reqs = this.createRequests(startDate, endDate, claimTypes) as GetPayerMixReportRequest[];
                    const generateOrFetch = this.features.isDemo
                        ? this.datasetGenerator.generatePayerMixReport
                        : this.datasetService.fetchPayerMixReport;
                    const items = _.flatten(_.compact(await Promise.all(_.map(reqs, generateOrFetch))));
                    payerMixChart = pri2ipmc(startDate, endDate, claimTypes, items as ProviderReportItem[]);

                    if (this.features.isDev) {
                        const payerMixInfo = _.concat(
                            [],
                            [
                                ['startDate', formatDate(payerMixChart.startDate, 'yyyy-MM-dd')],
                                ['endDate', formatDate(payerMixChart.endDate, 'yyyy-MM-dd')],
                            ],
                            _.map(payerMixChart.payers, payer => [payer.name, formatNumber('Currency')(payer.amount)]),
                        );
                        // tslint:disable: no-console
                        console.group('Payer Mix Data');
                        console.table(payerMixInfo);
                        console.groupEnd();
                        // tslint:enable: no-console
                    }
                } catch (err) {
                    payerMixChart = err;
                }
                runInAction(() => {
                    this.payerMixChart = payerMixChart;
                });
            },
        );
    };
    @action public loadClaimVolumeTimeline = (
        startDate: Date,
        endDate: Date,
        claimTypes: ClaimType[],
        span: SpanType,
    ): Promise<void> => {
        this.claimVolumeTimeline = undefined;
        return this.handleLoading(
            async (): Promise<void> => {
                let claimVolumeTimeline: IChartTimeline | Error;
                try {
                    const reqs = this.createRequests(
                        startDate,
                        endDate,
                        claimTypes,
                        span,
                    ) as GetGrossCollectionsReportRequest[];
                    const generateOrFetch = this.features.isDemo
                        ? this.datasetGenerator.generateGrossCollectionReport
                        : this.datasetService.fetchGrossCollectionReport;

                    const items = _.flatten(_.compact(await Promise.all(_.map(reqs, generateOrFetch))));
                    const labels = [ReportLabelType.CHARGE, ReportLabelType.PAYERPAID, ReportLabelType.PATIENTPAID];
                    claimVolumeTimeline = pri2ict(startDate, endDate, claimTypes, span, items, labels);
                } catch (err) {
                    claimVolumeTimeline = err;
                }
                runInAction(() => {
                    this.claimVolumeTimeline = claimVolumeTimeline;
                });
            },
        );
    };
    @action public loadPracticeSummaryTimeline = (
        startDate: Date,
        endDate: Date,
        claimTypes: ClaimType[],
        span: SpanType,
    ): Promise<void> => {
        this.practiceSummaryTimeline = undefined;
        return this.handleLoading(
            async (): Promise<void> => {
                let practiceSummaryTimeline: IChartTimeline | Error;
                try {
                    const reqs = this.createRequests(
                        startDate,
                        endDate,
                        claimTypes,
                        span,
                    ) as GetPracticeSummaryReportRequest[];
                    const generateOrFetch = this.features.isDemo
                        ? this.datasetGenerator.generatePracticeSummaryReport
                        : this.datasetService.fetchPracticeSummaryReport;
                    const items = _.flatten(_.compact(await Promise.all(_.map(reqs, generateOrFetch))));
                    const labels = [ReportLabelType.CHARGE, ReportLabelType.ADJUSTMENTS, ReportLabelType.PAYMENTS];
                    practiceSummaryTimeline = pri2ict(startDate, endDate, claimTypes, span, items, labels);
                } catch (err) {
                    practiceSummaryTimeline = err;
                }
                runInAction(() => {
                    this.practiceSummaryTimeline = practiceSummaryTimeline;
                });
            },
        );
    };
    @action public loadPatientCollectionsTimeline = (
        startDate: Date,
        endDate: Date,
        claimTypes: ClaimType[],
        span: SpanType,
    ): Promise<void> => {
        this.patientCollectionsTimeline = undefined;
        return this.handleLoading(
            async (): Promise<void> => {
                let patientCollectionsTimeline: IChartTimeline | Error;
                try {
                    const reqs = this.createRequests(
                        startDate,
                        endDate,
                        claimTypes,
                        span,
                    ) as GetPatientCollectionsReportRequest[];
                    const generateOrFetch = this.features.isDemo
                        ? this.datasetGenerator.generatePatientCollectionsReport
                        : this.datasetService.fetchPatientCollectionsReport;
                    const items = _.flatten(_.compact(await Promise.all(_.map(reqs, generateOrFetch))));
                    const labels = [
                        ReportLabelType.PATIENTRESPONSIBILITY,
                        ReportLabelType.PATIENTPAID,
                        ReportLabelType.PATIENTREFUND,
                    ];
                    patientCollectionsTimeline = pri2ict(startDate, endDate, claimTypes, span, items, labels);
                } catch (err) {
                    patientCollectionsTimeline = err;
                }
                runInAction(() => {
                    this.patientCollectionsTimeline = patientCollectionsTimeline;
                });
            },
        );
    };

    private loadMetric = async (
        metric: MetricType,
        submitted: ProviderReportItem[][],
        expected: ProviderReportItem[][],
        gross: ProviderReportItem[][],
    ): Promise<IDataset | undefined> => {
        try {
            switch (metric) {
                case MetricType.CLAIMS_SUBMITTED:
                    return this.loadClaimsSubmitted(submitted);
                case MetricType.PAYER_EXPECTED:
                    return this.loadPayerExpectedRate(expected);
                case MetricType.GROSS_CHARGE:
                    return this.loadGrossChargeRate(gross);
                case MetricType.GROSS_PAYER_PAID:
                    return this.loadGrossPayerPaidRate(gross);
                case MetricType.GROSS_PATIENT_PAID:
                    return this.loadGrossPatientPaidRate(gross);
                default:
                    throw new Error(`Unknown MetricType found: ${metric}`);
            }
        } catch (error) {
            return {
                metric,
                error,
            };
        }
    };

    private loadClaimsSubmitted = async (data: ProviderReportItem[][]): Promise<IDataset | undefined> => {
        const resolve = (items: ProviderReportItem[]): IFormulaProps => {
            return {
                submitted: this.sumByAmount(items, ReportLabelType.CLAIMSUBMITTED),
            };
        };
        const compute = (items: ProviderReportItem[]): number => {
            const r = resolve(items);
            return r.submitted;
        };
        const [prevValue, currValue] = _.map(data, compute);
        const [delta, sentiment] = this.computeDeltaAndSentiment(currValue, prevValue);
        return {
            metric: MetricType.CLAIMS_SUBMITTED,
            value: currValue,
            valueType: 'Integer',
            delta,
            sentiment,
            subtitled: true,
        };
    };
    private loadPayerExpectedRate = async (data: ProviderReportItem[][]): Promise<IDataset | undefined> => {
        const resolve = (items: ProviderReportItem[]): IFormulaProps => {
            return {
                expected: this.sumByAmount(items, ReportLabelType.EXPECTED),
            };
        };
        const compute = (items: ProviderReportItem[]): number => {
            const r = resolve(items);
            return r.expected;
        };
        const [prevValue, currValue] = _.map(data, compute);
        const [delta, sentiment] = this.computeDeltaAndSentiment(currValue, prevValue);
        return {
            metric: MetricType.PAYER_EXPECTED,
            value: currValue,
            valueType: 'Financial',
            delta,
            sentiment,
            subtitled: true,
        };
    };
    private loadGrossChargeRate = async (data: ProviderReportItem[][]): Promise<IDataset | undefined> => {
        const resolve = (items: ProviderReportItem[]): IFormulaProps => ({
            charges: this.sumByAmount(items, ReportLabelType.CHARGE),
        });
        const compute = (items: ProviderReportItem[]): number => {
            const r = resolve(items);
            return r.charges;
        };
        const [prevValue, currValue] = _.map(data, compute);
        const [delta, sentiment] = this.computeDeltaAndSentiment(currValue, prevValue);
        return {
            metric: MetricType.GROSS_CHARGE,
            value: currValue,
            valueType: 'Financial',
            delta,
            sentiment,
            subtitled: true,
        };
    };

    private loadGrossPayerPaidRate = async (data: ProviderReportItem[][]): Promise<IDataset | undefined> => {
        const resolve = (items: ProviderReportItem[]): IFormulaProps => {
            return {
                payer: this.sumByAmount(items, ReportLabelType.PAYERPAID),
            };
        };
        const compute = (items: ProviderReportItem[]): number => {
            const r = resolve(items);

            return r.payer;
        };

        const [prevValue, currValue] = _.map(data, compute);
        const [delta, sentiment] = this.computeDeltaAndSentiment(currValue, prevValue);
        return {
            metric: MetricType.GROSS_PAYER_PAID,
            value: currValue,
            valueType: 'Financial',
            delta,
            sentiment,
            subtitled: true,
        };
    };

    private loadGrossPatientPaidRate = async (data: ProviderReportItem[][]): Promise<IDataset | undefined> => {
        const resolve = (items: ProviderReportItem[]): IFormulaProps => {
            const patientPaid = this.sumByAmount(items, ReportLabelType.PATIENTPAID);
            const patientRefund = this.sumByAmount(items, ReportLabelType.PATIENTREFUND);
            return {
                patient: patientPaid + patientRefund,
            };
        };
        const compute = (items: ProviderReportItem[]): number => {
            const r = resolve(items);

            return r.patient;
        };

        const [prevValue, currValue] = _.map(data, compute);
        const [delta, sentiment] = this.computeDeltaAndSentiment(currValue, prevValue);
        return {
            metric: MetricType.GROSS_PATIENT_PAID,
            value: currValue,
            valueType: 'Financial',
            delta,
            sentiment,
            subtitled: true,
        };
    };

    private async findOrFetch(
        prefix: string,
        interval: d3.CountableTimeInterval,
        startDate: Date,
        endDate: Date,
        claimTypes: ClaimType[],
        fetch: (req: any) => Promise<ProviderReportItem[] | undefined>,
        generate: (req: any) => Promise<ProviderReportItem[] | undefined>,
    ): Promise<ProviderReportItem[][]> {
        const key = buildCacheKey({ prefix, startDate, endDate, claimTypes });
        let data: ProviderReportItem[][] | undefined = this.datasetIndex.get(key);
        if (!data) {
            const dateRanges = this.partitionDates(interval, startDate, endDate);
            const reqs = this.createRequests(dateRanges[0][0], dateRanges[1][1], claimTypes, SpanType.DAYOFYEAR);
            const generateOrFetch = this.features.isDemo ? generate : fetch;
            const items = _.flatten(_.compact(await Promise.all(_.map(reqs, generateOrFetch))));
            data = this.splitByDateRange(items, dateRanges, interval);
            this.datasetIndex.set(key, data);
        }
        return data;
    }

    private createRequests(startDate: Date, endDate: Date, claimTypes: ClaimType[], span?: SpanType): object[] {
        const baseReq: object = {
            start: formatDate(startDate, 'yyyy-MM-dd'),
            end: formatDate(endDate, 'yyyy-MM-dd'),
            span,
        };
        return claimTypes.map(claimType => _.assign({ claimType }, baseReq));
    }

    private partitionDates(interval: d3.CountableTimeInterval, startDate: Date, endDate: Date): Date[][] {
        const window = interval.count(startDate, endDate);
        const priorEnd = interval.offset(startDate, -1);
        const priorStart = interval.offset(priorEnd, -window);

        return [
            [priorStart, priorEnd],
            [startDate, endDate],
        ];
    }
    private splitByDateRange(
        items: ProviderReportItem[],
        dateRanges: Date[][],
        interval: d3.CountableTimeInterval,
    ): ProviderReportItem[][] {
        const date2doy = (dt: Date): [number, number] => {
            const first = d3.utcYear.floor(dt);
            return [dt.getUTCFullYear(), interval.count(first, dt) + 1];
        };
        return _.partition(items, item => {
            if (!_.isUndefined(item.group)) {
                const [year, day] = item.group.split('-'); // For DAYOFYEAR span it is year-day
                const [priorStartYear, priorStartDay] = date2doy(dateRanges[0][0]);
                const [priorEndYear, priorEndDay] = date2doy(dateRanges[0][1]);

                const date = new Date(parseInt(year, 10), 0, parseInt(day, 10));
                const priorStartDate = new Date(priorStartYear, 0, priorStartDay);
                const priorEndDate = new Date(priorEndYear, 0, priorEndDay);

                return priorStartDate <= date && date <= priorEndDate;
            }
        });
    }
    private sumByAmount(_items: ProviderReportItem[], label?: ReportLabelType): number {
        const items = label ? _.filter(_items, { label }) : _items;
        return _.sumBy(items, item => {
            const amount = Number.parseFloat(_.get(item, 'amount.amount'));
            return Number.isNaN(amount) ? 0 : amount;
        });
    }
    private computeDeltaAndSentiment(curr: number, prev: number, invert: boolean = false): [number, Sentiment] {
        const diff = curr - prev;
        const delta = prev !== 0 ? diff / prev : diff !== 0 ? diff / Math.abs(diff) : 0;
        const sentiment = delta === 0 ? 'neutral' : delta > 0 && !invert ? 'good' : 'bad';
        return [delta, sentiment];
    }

    private handleLoading = async (fn: () => Promise<void>): Promise<void> => {
        try {
            await fn();
        } catch (err) {
            handleError(err);
        }
    };
}
